import { useEffect, useState } from "react";
import { format } from "date-fns";
import WheelPicker from "../WheelPicker";
import { InfoDanger, ErrorIcon, ChevronDown } from "../../assets/svgs";
import PhoneNumberInput from "../PhoneNumberInput";
import useApp from "../../hooks/useApp";
import SelectOptionModal from "../SelectOptionModal";
import { safeJsonParse, stringToBoolean } from "../../utils";
import "./styles.scss";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const ALLOWED_CHARS = /^\d+$/;

const KEY_CODES = [9, 37, 39, 17, 86];

const FloatingInput = ({
  value,
  handleChange,
  type,
  placeholder,
  id,
  label,
  errorMsg,
  isInvalid = false,
  name,
  maxLength,
  isDecimal = false,
  options = [],
  optionModalHeading = "",
  isRequired = "",
  noteMsg = "",
  isTranslated,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const routeName = pathname.split("/")[1];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showSelectOptionModal, setShowSelectOptionModal] = useState(false);
  const { tenantConfig } = useApp();

  const handleKeyDown = (event) => {
    if (isDecimal && event.key === ".") {
      if (value.includes(".")) {
        event.preventDefault();
      }
      return;
    }
    if (
      !ALLOWED_CHARS.test(event.key) &&
      event.keyCode !== 8 &&
      !KEY_CODES.includes(event.keyCode)
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (tenantConfig?.phone_number_policy?.content.allowed_countries.length) {
      const allowedCountries =
        tenantConfig?.phone_number_policy?.content.allowed_countries;
      setSelectedCountry(allowedCountries[0]);
    }
  }, [tenantConfig?.phone_number_policy]);

  switch (type) {
    case "textfield":
      return (
        <>
          <div className="floating-input">
            <div className="form-floating">
              <input
                className="form-control custom-input"
                id={id}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                name={name}
                maxLength={maxLength}
              />
              <label htmlFor={id} className="custom-label">
                {label}{" "}
                {isRequired && (
                  <span className="is-required">
                    {t(routeName ? `${routeName}.required` : `app.required`)}
                  </span>
                )}
              </label>
            </div>
            {isInvalid && (
              <div className="error-container">
                <img src={InfoDanger} alt="info danger" />
                <span className="error-message">{errorMsg}</span>
              </div>
            )}
          </div>
        </>
      );
    case "number":
      return (
        <>
          <div className="floating-input">
            <div className="form-floating">
              <input
                className="form-control custom-input"
                id={id}
                placeholder={placeholder}
                value={value}
                onChange={(event) => {
                  const { value } = event.target;
                  if (isDecimal) {
                    event.target.value = value.replace(/[^0-9.]/g, "");
                  } else {
                    event.target.value = value.replace(/[^0-9]/g, "");
                  }
                  handleChange(event);
                }}
                name={name}
                maxLength={maxLength}
                onKeyDown={handleKeyDown}
              />
              <label htmlFor={id} className="custom-label">
                {label}{" "}
                {isRequired && (
                  <span className="is-required">
                    {t(routeName ? `${routeName}.required` : `app.required`)}
                  </span>
                )}
              </label>
              {isInvalid && (
                <img src={ErrorIcon} alt="error icon" className="error-icon" />
              )}
            </div>
            {isInvalid && (
              <div className="error-container">
                <span className="error-message">{errorMsg}</span>
              </div>
            )}
          </div>
        </>
      );
    case "email":
      return (
        <div className="floating-input">
          <div className="form-floating">
            <input
              type={type}
              className="form-control custom-input"
              id={id}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              name={name}
            />
            <label htmlFor={id} className="custom-label">
              {label}{" "}
              {isRequired && (
                <span className="is-required">
                  {t(routeName ? `${routeName}.required` : `app.required`)}
                </span>
              )}
            </label>
            {isInvalid && (
              <div className="error-container">
                <img src={InfoDanger} alt="info danger" />
                <span className="error-message">{errorMsg}</span>
              </div>
            )}
          </div>
        </div>
      );
    case "datetime":
      return (
        <div className="floating-input">
          <div className="form-floating">
            <input
              type={type}
              className="form-control custom-input"
              id={id}
              placeholder={placeholder}
              value={value && format(new Date(value), "dd/MM/yyyy")}
              name={name}
              onClick={() => setIsModalOpen(true)}
            />
            <label htmlFor={id} className="custom-label">
              {label}{" "}
              {isRequired && (
                <span className="is-required">
                  {t(routeName ? `${routeName}.required` : `app.required`)}
                </span>
              )}
            </label>
            {isInvalid && (
              <div className="invalid-feedback">
                <i className="bi bi-info-circle"></i>
                {errorMsg}
              </div>
            )}
          </div>
          <WheelPicker
            isModalOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={(e) => {
              handleChange(e);
              setIsModalOpen(false);
            }}
            name={name}
            value={value}
          />
        </div>
      );

    case "phoneNumber":
      return (
        <PhoneNumberInput
          isoCode={selectedCountry?.iso_code}
          countryCode={selectedCountry?.country_code}
          value={value}
          onChange={handleChange}
          name={name}
          maxLength={
            selectedCountry?.phone_number_length -
            selectedCountry?.country_code.length
          }
          error={errorMsg}
          ContainerStyle={{ marginLeft: 0, marginRight: 0 }}
          isInvalid={isInvalid}
          label={label}
          isRequired={isRequired}
        />
      );

    case "select": {
      const parsedOptions = safeJsonParse(options, []);
      const _isTranslated = stringToBoolean(isTranslated);
      const selectedOption = parsedOptions.find((opt) => opt.value === value);
      const selectedLabel =
        _isTranslated && selectedOption
          ? routeName
            ? t(`${routeName}.${selectedOption?.label}`)
            : t(`app.${selectedOption?.label}`)
          : selectedOption?.label || "";

      return (
        <div className="floating-input">
          <div className="form-floating">
            <input
              type={type}
              style={{ cursor: "pointer" }}
              className="form-control custom-input"
              id={id}
              placeholder={placeholder}
              value={selectedLabel}
              name={name}
              readOnly
              onClick={() => setShowSelectOptionModal(true)}
            />
            <label htmlFor={id} className="custom-label">
              {label}{" "}
              {isRequired && (
                <span className="is-required">
                  {t(routeName ? `${routeName}.required` : `app.required`)}
                </span>
              )}
            </label>
            <img
              src={ChevronDown}
              alt="chevron down icon"
              className="chevron-down-icon"
            />
            {isInvalid && (
              <div className="invalid-feedback">
                <i className="bi bi-info-circle"></i>
                {errorMsg}
              </div>
            )}
          </div>
          <SelectOptionModal
            name={name}
            value={value}
            visible={showSelectOptionModal}
            onClickBackdrop={() => setShowSelectOptionModal(false)}
            heading={optionModalHeading}
            options={parsedOptions}
            onSelect={(e) => {
              handleChange(e);
              setShowSelectOptionModal(false);
            }}
            isTranslated={_isTranslated}
          />
        </div>
      );
    }
    case "textarea":
      return (
        <div className="floating-input">
          <div className="form-floating">
            <textarea
              className="form-control custom-input"
              style={{ height: "unset" }}
              id={id}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              name={name}
              rows="3"
              maxLength={maxLength}
            />
            <label htmlFor={id} className="custom-label">
              {label}
            </label>
          </div>
          {noteMsg && <div className="note-text">{noteMsg}</div>}
          {isInvalid && (
            <div className="error-container">
              <img src={InfoDanger} alt="info danger" />
              <span className="error-message">{errorMsg}</span>
            </div>
          )}
        </div>
      );
    default:
      return null;
  }
};

export default FloatingInput;
